import React, {useEffect, useState} from "react";
import {Box, Chip, Stack, Typography} from "@mui/material";
import Button from "@mui/material/Button";
import {HugeiconsIcon} from "@hugeicons/react";
import {LockIcon} from "@hugeicons/core-free-icons";
import {FlowProtectionRepository} from "../../../repositories/FlowProtectionRepository";
import {FlowProtectionPasswordModal} from "../../../components/modals/FlowProtectionPasswordModal";

/**
 * Project settings card for the per-flow message protection password
 * (gates seeing protected user message content in plaintext elsewhere
 * in the app - see FlowProtectionButton).
 */
export const FlowProtectionSettings = ({project}) => {
  const flowUid = project?.id;
  const [configured, setConfigured] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchStatus = () => {
    if (!flowUid) return;
    FlowProtectionRepository.getStatus(flowUid)
      .then(response => setConfigured(!!response?.data?.configured))
      .catch(() => setConfigured(null));
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowUid]);

  return (
    <>
      <Stack
        direction={{xs: "column", sm: "row"}}
        justifyContent={"space-between"}
        alignItems={{xs: "flex-start", sm: "center"}}
        gap={2}
        sx={{borderRadius: {xs: 2, md: 4}, p: {xs: 2, md: 3}}}
        className={"border-shadow"}
      >
        <Stack direction={"row"} gap={2} alignItems={"flex-start"}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: 2.5,
            flexShrink: 0,
            bgcolor: "rgba(132, 145, 247, 0.12)",
            color: "primary.main"
          }}>
            <HugeiconsIcon icon={LockIcon} size={20}/>
          </Box>
          <Box>
            <Stack direction={"row"} gap={1} alignItems={"center"} flexWrap={"wrap"}>
              <Typography fontWeight={600}>Flow Protection</Typography>
              {configured !== null &&
                <Chip
                  size={"small"}
                  label={configured ? "Configured" : "Not set"}
                  color={configured ? "success" : "default"}
                  variant={configured ? "filled" : "outlined"}
                  sx={{fontWeight: 500, height: 20, "& .MuiChip-label": {px: 1}}}
                />
              }
            </Stack>
            <Typography variant={"body2"} color={"text.secondary"} sx={{mt: 0.25}}>
              {configured
                ? "A password is set for this project. Rotate it any time - the current password is required."
                : "Set a password to gate access to protected user messages in the dashboard and supervision chat."}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant={configured ? "ghost" : "primary"}
          color={configured ? "secondary" : "primary"}
          sx={{borderRadius: 2, flexShrink: 0}}
          onClick={() => setModalOpen(true)}
          disabled={!flowUid || configured === null}
        >
          {configured ? "Update Password" : "Set Password"}
        </Button>
      </Stack>
      <FlowProtectionPasswordModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        flowUid={flowUid}
        mode={configured ? "update" : "set"}
        onSuccess={fetchStatus}
      />
    </>
  );
};
